import { cx } from '../../../utils/cx';
import { Badge } from '../../base/badges/badges';
import { getInitialsFromName } from '../../base/avatar/avatar';
import { NavAccountCard } from '../../application/app-navigation/base-components/nav-account-card';

type Account = {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  orgLogoUrl?: string;
  orgName?: string;
  profile?: {
    avatarUrl?: string | null;
  } | null;
};

export type SideNavigationProps = {
  account?: Account | null;
  link: React.ElementType;
  className?: string;
  logoUrl?: string;
  homeLink: string;
  avatarGroupLink?: string;
  renderNavItems: () => React.ReactNode;
  renderBottomNavItems?: () => React.ReactNode;
  onSignOut?: (e: React.FormEvent) => void;
};

export type SideNavigationItemProps = {
  link: React.ElementType;
  href: string;
  label: string;
  icon?: React.ReactNode;
  count?: number;
};

export function SideNavigationItem({
  link: Link,
  href,
  label,
  icon,
  count
}: SideNavigationItemProps) {
  return (
    <Link
      href={href}
      className="flex align-center gap-2 justify-between px-3 py-2"
    >
      <span className="flex-1 flex align-center gap-2">
        {!!icon && <span className="text-fg-quaternary">{icon}</span>}
        <span className="text-md font-semibold text-tertiary">{label}</span>
      </span>
      {typeof count === 'number' && (
        <Badge size="sm" color="gray" type="pill-color">
          {count}
        </Badge>
      )}
    </Link>
  );
}

export function SideNavigation({
  account,
  link: Link,
  homeLink,
  avatarGroupLink,
  className,
  logoUrl,
  renderNavItems,
  renderBottomNavItems,
  onSignOut = (e) => {
    e.preventDefault();
  }
}: SideNavigationProps) {
  const fullName = account
    ? `${account.firstName} ${account.lastName}`
    : undefined;
  const initials = getInitialsFromName(fullName);
  return (
    <aside
      className={cx(
        'h-full flex flex-col border-r bg-secondary border-secondary overflow-y-auto',
        className
      )}
    >
      <div className="flex-1 flex flex-col">
        <div className="px-5 pt-6">
          {!!logoUrl && (
            <Link
              href={homeLink}
              aria-label="Go to homepage"
              className="rounded-xs outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              {!!logoUrl && <img src={logoUrl} alt="Logo" className="h-10" />}
            </Link>
          )}
        </div>
        <div className="flex-1 flex flex-col p-4 justify-between">
          <div>{renderNavItems()}</div>
          <div>
            {renderBottomNavItems?.()}
            {account && (
              <div className="mt-4.5 bg-primary">
                <NavAccountCard
                  popoverPlacement="end top"
                  selectedAccountId={account.id}
                  link={Link}
                  avatarGroupLink={avatarGroupLink}
                  onSignOut={onSignOut}
                  accounts={[
                    {
                      id: account.id,
                      name: `${account.firstName} ${account.lastName}`,
                      email: account.email || '',
                      avatar: account.profile?.avatarUrl || undefined,
                      initials,
                      orgLogoUrl: account.orgLogoUrl,
                      orgName: account.orgName
                    }
                  ]}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}
